import React, { useState, useContext, ChangeEvent, useMemo } from "react";
import { Box, Typography, Button, Stack, Divider } from "@mui/material";
import { ImportWizardContext } from "components/importer/ImportWizardContext";
import { WIZARD_ACTION, DataWidget } from "types";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { useTranslation } from "react-i18next";
import AuturaTextField from "components/inputs/AuturaTextField";
import AuturaDropdown from "components/inputs/AuturaDropdown";
dayjs.extend(customParseFormat);

interface CombineFieldsWidgetProps {
  setDataWidget: (value: DataWidget) => void;
}

export const CombineFieldsWidget: React.FC<CombineFieldsWidgetProps> = ({
  setDataWidget,
}) => {
  const { t } = useTranslation();
  const { state, dispatch } = useContext(ImportWizardContext);
  const [fields, setFields] = useState<
    { csvField: string; trailingCharacter: string }[]
  >([
    { csvField: "", trailingCharacter: "" },
    { csvField: "", trailingCharacter: "" },
  ]);

  // this component is not used if the user has not selected a system field
  if (!state.selectedSystemField) {
    setDataWidget("none");
    return null;
  }
  const systemField = state.selectedSystemField;
  const handleMapCombinedFields = () => {
    dispatch({
      type: WIZARD_ACTION.MAP_COMBINED_FIELDS,
      payload: {
        systemField,
        fields,
      },
    });
    setDataWidget("preview");
  };

  const combinedFieldValue = useMemo(() => {
    return fields
      .map(({ csvField, trailingCharacter }) => {
        return `${csvField}${trailingCharacter}`;
      })
      .join("");
  }, [fields]);

  return (
    <Stack direction="column" spacing={0}>
      <Divider textAlign={"center"} role={"presentation"}>
        <Typography variant={"overline"}>
          {t("combine_fields", { ns: "importWizard" })}
        </Typography>
      </Divider>
      {fields.map(({ csvField, trailingCharacter }, index) => (
        <Stack direction="row" gap={1.5} display="flex" pb={2} key={index}>
          <Box sx={{ flex: 2 }}>
            <AuturaDropdown
              name={`csvField-${index}`}
              options={state.csvHeaders}
              value={csvField || ""}
              label={t("csvField", { ns: "importWizard" })}
              setValue={(e: ChangeEvent<HTMLInputElement>) => {
                const newFields = [...fields];
                newFields[index].csvField = e.target.value;
                setFields(newFields);
              }}
              menuItemProps={{
                divider: true,
              }}
            />
          </Box>
          <AuturaTextField
            name={`trailingCharacter-${index}`}
            label={t("trailingCharacters", { ns: "importWizard" })}
            value={trailingCharacter || ""}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              const newFields = [...fields];
              newFields[index].trailingCharacter = e.target.value;
              setFields(newFields);
            }}
            sx={{ flex: 1 }}
          />
        </Stack>
      ))}
      <Button
        onClick={() =>
          setFields([...fields, { csvField: "", trailingCharacter: "" }])
        }
        fullWidth
        variant="contained"
        sx={{ p: 0 }}
      >
        <Typography variant="subtitle1" fontWeight="bold">
          {t("buttons.add_combined_field", { ns: "importWizard" })}
        </Typography>
      </Button>
      <Stack direction="row" spacing={3} display="flex" pt={4}>
        <Box sx={{ flex: 1 }}>
          <AuturaTextField
            name="combined field"
            placeholder={t("combinedValue", { ns: "importWizard" })}
            value={combinedFieldValue}
            readonly
          />
        </Box>
      </Stack>
      <Stack
        direction="row"
        gap={4}
        display="flex"
        justifyContent="space-around"
      >
        <Button
          onClick={() => handleMapCombinedFields()}
          variant="text"
          color="primary"
          disabled={fields.length < 2}
        >
          {t("buttons.map_combined_fields", { ns: "importWizard" })}
        </Button>
      </Stack>
    </Stack>
  );
};
